import { Injectable } from '@angular/core';

import Amplify, { Auth } from 'aws-amplify';

import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { from } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import myconfig from '../config';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

   public isLoggedIn: BehaviorSubject<boolean>;             // asynchronous flag

   constructor() {
      // configure AWS Amplify
      Amplify.configure(myconfig);
      // initialize the asynchronous flag
      this.isLoggedIn = new BehaviorSubject<boolean>(false);
   }

   // performs login and in case of success sets this.isLoggedIn
   // note: we tap in the callback so the possible error is passed to the caller
   public login(username, password): Observable<any> {
      return from(Auth.signIn(username, password)).pipe(
         tap(() => this.isLoggedIn.next(true))    // set the asynchronous flag
      );
   }  // end login (ex signIn)

   // performs logout and in case of success resets this.isLoggedIn
   // note: we tap in the callback so the possible error is passed to the caller
   public logout(): Observable<any> {
      return from(Auth.signOut()).pipe(
         tap(() => this.isLoggedIn.next(false))    // reset the asynchronous flag
      );
   }  // end logout (ex signOut)

   // gets the authentication state from the Auth service object of AWS Amplify,
   // sets/resets this.isLoggedIn accordingly
   // and returns it as an Observable<boolean>
   // Note1: this method will be typically invoked once at inizialization
   // (e.g. by ngOnInit of AppComponent) and multiple times when navigating
   // to protected components (e.g. by canActivate of AuthGuard)
   // Note2: the bypassCache arg is set to false, so we actually read from localStorage
   public isAuthenticated(): Observable<boolean> {
      // alert("AuthService: isAuthenticated method called...");          // DEBUG
      return from(Auth.currentAuthenticatedUser({bypassCache: false })).pipe(
        map(result => {
          this.isLoggedIn.next(true);
          return true;
        }),
        catchError(error => {
          this.isLoggedIn.next(false);
          return of(false);
        })
      );
   }  // end isAuthenticated (ex testAuthentication)

   // performs registration
   // note: the returned Observable is passed to the caller, which will handle it
   public register(username, email, surname, name, password): Observable<any> {
      if(name == "") { name = " "};
      return from(Auth.signUp(
        {
          "username": username,
          "password": password,
          attributes: {
            "given_name": name,
            "family_name": surname,
            "email": email
          }
        }
      ));
   }  // end register

   // performs confirmation
   // note: the returned Observable is passed to the caller, which will handle it
   public confirm(username, code): Observable<any> {
      return from(Auth.confirmSignUp(username, code));
   }  // end confirm

   // gets user's info
   // note: the returned Observable is passed to the caller, which will handle it
   public getUserInfo(): Observable<any> {
     return from(Auth.currentUserInfo());
   }

   // get current auth session
   // note: the returned Observable is passed to the caller, which will handle it
   public getCurrentSession(): Observable<any> {
     return from(
       Auth.currentSession()
     ).pipe(
        tap(() => console.log("getCurrentSession"))
     );
   }

   public changePassword(oldPassword, newPassword): Observable<any> {
     return from(
       Auth.currentAuthenticatedUser()
       .then(user => {
         return Auth.changePassword(user, oldPassword, newPassword);
       })
    )
   }

   public forgotPassword(username): Observable<any> {
     return from(
       Auth.forgotPassword(username)
     )
   }

   public changeForgotPassword(username, code, newPassword): Observable<any> {
     return from(
       Auth.forgotPasswordSubmit(username, code, newPassword)
     )
   }

}  // end AuthService
